import checkPermissionConfig from "@/utils/checkPermissionConfig/index"
import { useUserStore } from "@/stores/user"
import type { RouteLocationNormalized } from "vue-router"

interface RouterProtectOptions {
    to: RouteLocationNormalized
    goBackPath?: { name: string }
    PERMISSIONS: string | string[]
    api: (id: unknown) => Promise<{ data: AssetResponse }>
    formatFunction?: (response: { data: AssetResponse }) => {
        data: AssetResponse
    }
}

interface AssetResponse {
    creator?: {
        department?: { uuid?: string }
        uuid?: string
        role?: { level?: number }
    }
    status?: string
    [key: string]: unknown
}

/**
 * 保護路由的實用函式，根據使用者權限進行判斷。
 * @param options - 選項設定。
 * @param options.to - 目標路由的路由物件。
 * @param options.goBackPath - 在權限不足時的備用路徑。
 * @param options.PERMISSIONS - 所需權限的設定。
 * @param options.api - 用於獲取資料的 API 函式。
 * @param options.formatFunction - 用戶 format Api response 讓其符合 router protect 驗證的格式
 * @returns 若使用者具有權限則返回 [true, response]，否則返回 [goBackPath]。
 */
export default async function useRouterProtect({
    to,
    goBackPath = { name: "forbidden-index" },
    PERMISSIONS,
    api,
    formatFunction,
}: RouterProtectOptions): Promise<
    [true, { data: AssetResponse }] | [{ name: string }]
> {
    try {
        const response = await api(to.params.id)
        const formatResponse = formatFunction
            ? formatFunction(response)
            : response
        const userStore = useUserStore()

        const hasPermissions = checkPermissionConfig(PERMISSIONS, userStore, {
            department: formatResponse.data.creator?.department?.uuid,
            status: formatResponse.data.status,
            creatorUuid: formatResponse.data.creator?.uuid,
            roleLevel: formatResponse.data.creator?.role?.level,
        })

        if (!hasPermissions) throw new Error("無權限訪問此頁面")
        return [true, response]
    } catch (error) {
        console.error(error)
        return [goBackPath]
    }
}
